import React, { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const MIN_YEAR = 2025;

const ModernMonthPicker = ({ value, onChange, max, ariaLabel }) => {
  const maxYear = Number((max || "").slice(0, 4)) || new Date().getFullYear();
  const selectedYear = Number((value || "").slice(0, 4));
  const [visibleYear, setVisibleYear] = useState(selectedYear || maxYear);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => { if (selectedYear) setVisibleYear(selectedYear); }, [selectedYear]);
  useEffect(() => {
    const closePicker = (event) => { if (!containerRef.current?.contains(event.target)) setIsOpen(false); };
    const closeWithEscape = (event) => { if (event.key === "Escape") setIsOpen(false); };
    document.addEventListener("mousedown", closePicker);
    document.addEventListener("keydown", closeWithEscape);
    return () => { document.removeEventListener("mousedown", closePicker); document.removeEventListener("keydown", closeWithEscape); };
  }, []);

  const displayValue = value ? `${MONTHS[Number(value.slice(5, 7)) - 1]} ${value.slice(0, 4)}` : "Mois et année";

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <button type="button" onClick={() => setIsOpen((open) => !open)} className="flex h-12 w-full min-w-56 items-center rounded-xl border border-gray-200 bg-white p-3 pr-20 text-left shadow-sm transition-all duration-200 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400" aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={isOpen}>
          <span className={value ? "text-gray-900" : "text-gray-400"}>{displayValue}</span>
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="absolute right-10 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Effacer la période"><X className="h-4 w-4" /></button>}
        <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
      </div>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-2 w-80 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl" role="dialog" aria-label={ariaLabel}>
          <div className="mb-4 flex items-center justify-between gap-2">
            <button type="button" disabled={visibleYear <= MIN_YEAR} onClick={() => setVisibleYear((year) => year - 1)} className="rounded-lg p-2 text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300" aria-label="Année précédente"><ChevronLeft className="h-5 w-5" /></button>
            <span className="min-w-20 text-center text-base font-semibold text-blue-900">{visibleYear}</span>
            <button type="button" disabled={visibleYear >= maxYear} onClick={() => setVisibleYear((year) => year + 1)} className="rounded-lg p-2 text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300" aria-label="Année suivante"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((month, index) => {
              const monthValue = `${visibleYear}-${String(index + 1).padStart(2, "0")}`;
              const unavailable = Boolean(max && monthValue > max);
              const selected = value === monthValue;
              return <button key={month} type="button" disabled={unavailable} onClick={() => { onChange(monthValue); setIsOpen(false); }} className={`rounded-lg px-2 py-2.5 text-sm transition ${unavailable ? "cursor-not-allowed bg-gray-100 text-gray-300" : selected ? "bg-blue-600 font-semibold text-white" : "text-gray-700 hover:bg-blue-100 hover:text-blue-800"}`}>{month}</button>;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernMonthPicker;